/**
 * Full-screen success view shown after a destructive account action
 * (Figma 9410:8538): a centred card on the canvas background with an
 * illustration, a title, a short message and a single primary action.
 *
 * `WorkspaceDeletedView` is the workspace flavour: with other workspaces left
 * the user continues into the first one (Figma 9692:32321), otherwise they
 * are sent to the marketing site (Figma 9692:32395).  `DeletionSuccessView`
 * is the generic card reused by account deletion, which passes its own
 * illustration (Figma 9410:8538).
 */
import React from 'react';
import { Button } from '@spark';
import workspaceDeletedRemainingIllustration from '@/shared/assets/illustrations/workspace-deleted-remaining.svg';
import workspaceDeletedLastIllustration from '@/shared/assets/illustrations/workspace-deleted-last.svg';

export const SPECIFIC_AI_WEBSITE = 'https://specific.ai/';
export const WORKSPACE_DELETED_TITLE = 'You have successfully deleted your workspace';
export const WORKSPACE_DELETED_CONTINUE = 'Continue working in one of your other workspaces.';
export const WORKSPACE_DELETED_GOODBYE = 'We would love to see you here in the future';

export function goToWebsite() {
    window.location.assign(SPECIFIC_AI_WEBSITE);
}

/**
 * @param {string} [illustration] URL of the decorative artwork rendered above
 *   the title (an imported SVG asset).  Omitted → no artwork.
 */
export function DeletionSuccessView({ illustration, title, message, buttonLabel, onAction, busy = false }) {
    return (
        <div className="deletion-success" role="dialog" aria-modal="true" aria-labelledby="deletion-success-title">
            <div className="deletion-success__card">
                {illustration && (
                    <img
                        src={illustration}
                        alt=""
                        aria-hidden="true"
                        draggable={false}
                        className="deletion-success__illustration"
                        data-testid="deletion-success-illustration"
                    />
                )}
                <div className="deletion-success__text">
                    <h1 id="deletion-success-title" className="deletion-success__title">
                        {title}
                    </h1>
                    <p className="deletion-success__message">{message}</p>
                </div>
                <Button type="button" variant="primary" size="large" disabled={busy} onClick={onAction}>
                    {buttonLabel}
                </Button>
            </div>
        </div>
    );
}

export default function WorkspaceDeletedView({ remainingWorkspaces = [], onContinue, busy = false }) {
    const next = remainingWorkspaces[0];
    if (!next) {
        return (
            <DeletionSuccessView
                illustration={workspaceDeletedLastIllustration}
                title={WORKSPACE_DELETED_TITLE}
                message={WORKSPACE_DELETED_GOODBYE}
                buttonLabel="Go to website"
                onAction={goToWebsite}
                busy={busy}
            />
        );
    }
    return (
        <DeletionSuccessView
            illustration={workspaceDeletedRemainingIllustration}
            title={WORKSPACE_DELETED_TITLE}
            message={WORKSPACE_DELETED_CONTINUE}
            buttonLabel="Continue"
            onAction={() => onContinue?.(next)}
            busy={busy}
        />
    );
}
